import { BASE_URL, environment } from "utils/constant";
import { LuUserMinus } from "react-icons/lu";
import article from "../../assets/permissions/article.svg";
import group from "../../assets/permissions/group.svg";
import finance from "../../assets/permissions/credit_score.svg";
import { IoCheckbox } from "react-icons/io5";
import { MdCheckBoxOutlineBlank } from "react-icons/md";
import { useAlert } from "../../components/UI_components/Alert";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { X, Plus, Check, User } from "lucide-react";
import { rtdb } from "utils/firebase";
import { getInitials } from "utils/tasks/taskHelpers";
import { useLocation } from "react-router-dom";

// InfoTooltip removed — descriptions are shown inline below the label

// ─── Permission Descriptions ─────────────────────────────────────────────────
const PERMISSION_DESCRIPTIONS = {
  is_admin: "Grants full access to all features, settings, and team controls.",
  view_all: "Allows viewing all modules: materials, workers, and finance.",
  view_material_management:
    "Allows viewing material inventory, purchases, and usage logs.",
  view_worker_management:
    "Allows viewing worker profiles, attendance, and daily wages.",
  view_finance:
    "Allows viewing financial transactions, budgets, and cost reports.",
  edit_all:
    "Grants edit access to all modules. Automatically enables all view permissions as well.",
  edit_material_management:
    "Allows adding, updating, and deleting material entries.",
  edit_worker_management: "Allows managing worker records and attendance.",
  edit_finance: "Allows creating and modifying financial entries.",
  create_taskgroup:
    "Allows creating and assigning task groups to team members.",
  report_generation: "Allows generating and downloading site progress reports.",
  member_control:
    "Allows adding, removing, and editing team member permissions.",
};

const SITE_PERMISSION_COLUMNS = [
  { key: "is_admin", label: "Admin" },
  { key: "view_material_management", label: "View Materials" },
  { key: "edit_material_management", label: "Edit Materials" },
  { key: "view_worker_management", label: "View Workers" },
  { key: "edit_worker_management", label: "Edit Workers" },
  { key: "view_finance", label: "View Finance" },
  { key: "edit_finance", label: "Edit Finance" },
  { key: "create_taskgroup", label: "Tasks" },
  { key: "report_generation", label: "Reports" },
  { key: "member_control", label: "Team" },
];

const SitePermissionIndicator = ({ checked }) =>
  checked ? (
    <IoCheckbox className="h-5 w-5 text-btn-bg" />
  ) : (
    <MdCheckBoxOutlineBlank className="h-5 w-5 text-[#C6BCD0]" />
  );

const getStoredJson = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch (error) {
    return null;
  }
};

const getMemberUser = (member) => member?.user || member || {};

const getMemberName = (member) => {
  const user = getMemberUser(member);
  const firstName = member?.first_name || user?.first_name || user?.name || "";
  const lastName = member?.last_name || user?.last_name || "";
  return `${firstName} ${lastName}`.trim() || "Unnamed member";
};

const getMemberEmail = (member) => {
  const user = getMemberUser(member);
  return member?.email || user?.email || user?.user_email || "N/A";
};

const getMemberProfession = (member) => {
  const user = getMemberUser(member);
  if (member?.joined_as === "owner" || user?.profession === "owner") {
    return "Owner";
  }
  if (member?.is_admin) return "Admin";
  return member?.profession || user?.profession || "Member";
};

const getMemberAvatar = (member) => {
  const user = getMemberUser(member);
  return member?.userpic || user?.userpic;
};

const getMemberLookupIds = (member) =>
  [
    member?.member_id,
    member?.id,
    member?.organization_member_id,
    member?.user_id,
    member?.user?.id,
  ]
    .filter((id) => id !== undefined && id !== null && id !== "")
    .map(String);

export default function ProjectTeamManagement({
  userPermissions,
  permissionsLoading,
}) {
  const location = useLocation();
  const [teams, setTeams] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const handledMemberRequestRef = useRef(null);
  const { addAlert } = useAlert();
  const memberSearchParams = new URLSearchParams(location.search);
  const requestedMemberId = memberSearchParams.get("memberId");
  const requestedMemberKey = requestedMemberId
    ? `${requestedMemberId}:${memberSearchParams.get("memberRequest") || ""}`
    : null;

  const storedUser = getStoredJson("user");
  const currentMember = teams.find(
    (member) =>
      member?.user_id === storedUser?.user ||
      member?.user?.id === storedUser?.user ||
      member?.user?.email === storedUser?.user_email ||
      member?.email === storedUser?.user_email,
  );

  const canManageMembers =
    currentMember?.joined_as === "owner" ||
    currentMember?.is_admin ||
    currentMember?.member_control ||
    userPermissions?.is_admin ||
    userPermissions?.member_control;

  const canManageSpecificMember = useCallback(
    (member) =>
      member?.joined_as !== "owner" &&
      member?.user?.profession !== "owner" &&
      member?.user_id !== storedUser?.user &&
      member?.user?.id !== storedUser?.user &&
      getMemberEmail(member) !== storedUser?.user_email,
    [storedUser?.user, storedUser?.user_email],
  );

  const currentMemberName = currentMember
    ? getMemberName(currentMember)
    : "Project member";
  const currentMemberEmail = currentMember
    ? getMemberEmail(currentMember)
    : "Fetching your project access";
  const currentMemberProfession = currentMember
    ? getMemberProfession(currentMember)
    : "Member";

  const getTeamMembers = async () => {
    if (teams.length === 0) {
      setPageLoading(true);
    }

    try {
      const accessToken = localStorage.getItem("access");
      const siteId = localStorage.getItem("site");
      const organizationId = getStoredJson("organization")?.id;

      if (!accessToken || !siteId) return;

      const [teamRes, orgRes] = await Promise.all([
        fetch(`${BASE_URL}/team/list-site-members/${siteId}/`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }),
        organizationId
          ? fetch(`${BASE_URL}/org/organization/${organizationId}/members/`, {
              method: "GET",
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            })
          : Promise.resolve({
              ok: true,
              json: async () => ({ status: true, data: [] }),
            }),
      ]);

      const teamData = await teamRes.json();
      const orgData = await orgRes.json();

      if (!teamData.status) {
        throw new Error(teamData.message);
      }

      const orgMembersById = Array.isArray(orgData?.data)
        ? orgData.data.reduce((acc, orgMember) => {
            acc[orgMember.id] = orgMember;
            return acc;
          }, {})
        : {};

      const enriched = (teamData.data || []).map((teamMember) => {
        const orgMember = orgMembersById[teamMember.organization_member_id];
        if (!orgMember) return teamMember;
        return {
          ...teamMember,
          user: teamMember.user || orgMember.user,
          profession:
            teamMember.profession || orgMember?.user?.profession || "Member",
        };
      });

      const sorted = enriched.sort((a, b) => {
        const isAYou =
          a.user_id === storedUser?.user || a.user?.email === storedUser?.user_email;
        const isBYou =
          b.user_id === storedUser?.user || b.user?.email === storedUser?.user_email;
        if (isAYou && !isBYou) return -1;
        if (!isAYou && isBYou) return 1;
        return getMemberName(a).localeCompare(getMemberName(b));
      });

      setTeams(sorted);
    } catch (error) {
      addAlert(error?.message || "Error fetching teams", "error");
      setTeams([]);
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    getTeamMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handledMemberRequestRef.current = null;
  }, [requestedMemberKey]);

  useEffect(() => {
    if (
      !requestedMemberId ||
      !requestedMemberKey ||
      pageLoading ||
      permissionsLoading ||
      teams.length === 0 ||
      handledMemberRequestRef.current === requestedMemberKey
    ) {
      return;
    }

    const requestedMember = teams.find((member) =>
      getMemberLookupIds(member).includes(String(requestedMemberId)),
    );

    handledMemberRequestRef.current = requestedMemberKey;

    if (!requestedMember) {
      addAlert("Selected team member was not found", "warning");
      return;
    }

    if (
      !canManageMembers ||
      !canManageSpecificMember(requestedMember)
    ) {
      addAlert("You do not have permission to manage this member", "warning");
      return;
    }

    setSelectedMember(requestedMember);
  }, [
    requestedMemberId,
    requestedMemberKey,
    pageLoading,
    permissionsLoading,
    teams,
    canManageMembers,
    canManageSpecificMember,
    addAlert,
  ]);

  return (
    <div className="relative min-h-full px-6 py-6">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold text-gray-900">Project Team</h1>
        <p className="text-sm text-muted-foreground">
          Review site-level access and manage project member permissions
        </p>
      </div>

      {/* <div className="mb-4 flex w-full items-center rounded-2xl bg-white p-5">
        <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-full bg-indigo-100 text-2xl font-bold text-indigo-600">
          {getMemberAvatar(currentMember) ? (
            <img
              src={getMemberAvatar(currentMember)}
              alt={currentMemberName}
              className="h-14 w-14 rounded-full object-cover"
            />
          ) : (
            getInitials(currentMemberName)
          )}
        </div>
        <div className="ml-5 min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-lg font-medium text-gray-800">
            {currentMemberName} {"(You)"}
            <span className="rounded-full bg-[#F3F0F5] px-2 py-0.5 text-xs font-medium text-btn-bg">
              {currentMemberProfession}
            </span>
          </div>
          <div className="mt-0.5 truncate text-sm text-gray-500">
            {currentMemberEmail}
          </div>
        </div>
      </div> */}

      <div className="rounded-2xl bg-white">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="font-normal text-[#737373]">
            {`${teams.length} project member${teams.length !== 1 ? "s" : ""}`}
          </div>
          {canManageMembers && (
            <button
              className="flex flex-row items-center rounded-lg bg-btn-bg px-3 py-2 font-medium text-white hover:bg-purple-900"
              onClick={() => setShowAddMemberModal(true)}
            >
              <div className="mr-2 rounded-full bg-[#58267F] p-1">
                <Plus className="h-4 w-4" />
              </div>
              New Member
            </button>
          )}
        </div>

        <div className="h-[calc(90vh-200px)] overflow-auto px-3 pb-5">
          <div className="overflow-x-auto rounded-md border border-[#E7E1EC]">
            <table className="w-full border-collapse">
              <thead className="bg-[#FAF7FC]">
                <tr className="border-b border-[#E7E1EC]">
                  <th className="sticky left-0 z-10 bg-[#FAF7FC] px-3 py-2 text-left text-xs font-semibold tracking-[0.08em] text-[#6D6475]">
                    Member
                  </th>
                  {SITE_PERMISSION_COLUMNS.map((column) => (
                    <th
                      key={column.key}
                      className="px-3 py-2 text-center text-[11px] font-semibold tracking-[0.08em] text-[#6D6475]"
                    >
                      {column.label}
                    </th>
                  ))}
                  <th className="sticky right-0 z-10 bg-[#FAF7FC] px-3 py-2 text-center text-xs font-semibold tracking-[0.08em] text-[#6D6475]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {pageLoading || permissionsLoading ? (
                  <tr>
                    <td
                      className="px-4 py-10 text-center text-sm text-gray-500"
                      colSpan={SITE_PERMISSION_COLUMNS.length + 2}
                    >
                      Loading project team...
                    </td>
                  </tr>
                ) : teams.length === 0 ? (
                  <tr>
                    <td
                      className="px-4 py-10 text-center text-sm text-gray-500"
                      colSpan={SITE_PERMISSION_COLUMNS.length + 2}
                    >
                      No project members found.
                    </td>
                  </tr>
                ) : (
                  teams.map((member, index) => (
                    <tr
                      key={member.member_id || member.id}
                      className={
                        index !== teams.length - 1
                          ? "border-b border-[#F0EBF4]"
                          : ""
                      }
                    >
                      <td className="sticky left-0 z-[1] bg-white px-3 py-3">
                        <div className="flex items-center gap-3">
                          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100 text-sm font-bold text-gray-600">
                            {getMemberAvatar(member) ? (
                              <img
                                src={getMemberAvatar(member)}
                                alt={getMemberName(member)}
                                className="h-11 w-11 rounded-full object-cover"
                              />
                            ) : (
                              getInitials(getMemberName(member))
                            )}
                          </div>
                          <div className="min-w-[190px]">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="truncate text-sm font-semibold text-[#2D2832]">
                                {getMemberName(member)}
                                {member?.user_id === storedUser?.user
                                  ? " (You)"
                                  : ""}
                              </p>
                              <span className="rounded-full bg-[#F3F0F5] px-2 py-0.5 text-[11px] font-medium text-btn-bg">
                                {getMemberProfession(member)}
                              </span>
                            </div>
                            <p
                              className="truncate text-xs text-[#7A7480]"
                              title={getMemberEmail(member)}
                            >
                              {getMemberEmail(member)}
                            </p>
                          </div>
                        </div>
                      </td>
                      {SITE_PERMISSION_COLUMNS.map((column) => (
                        <td key={column.key} className="px-3 py-3 text-center">
                          <div className="flex justify-center">
                            <SitePermissionIndicator
                              checked={Boolean(member?.[column.key])}
                            />
                          </div>
                        </td>
                      ))}
                      <td className="sticky right-0 z-[1] bg-white px-3 py-3">
                        <div className="flex items-center justify-center">
                          {canManageMembers && canManageSpecificMember(member) && (
                            <button
                              className="inline-flex items-center gap-1 rounded-full border border-btn-bg px-3 py-1.5 text-xs font-semibold text-btn-bg transition-all hover:bg-purple-50"
                              onClick={() => setSelectedMember(member)}
                            >
                              Manage
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showAddMemberModal && (
        <AddMemberModal
          onClose={() => setShowAddMemberModal(false)}
          teams={teams}
          onSave={getTeamMembers}
        />
      )}

      {selectedMember && (
        <AccessManagementModal
          member={selectedMember}
          onClose={() => setSelectedMember(null)}
          onSave={getTeamMembers}
          variant="drawer"
        />
      )}
    </div>
  );
}

export const AddMemberModal = ({ onClose, onSave, teams = [] }) => {
  const [organizationMembers, setOrganizationMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showPermissions, setShowPermissions] = useState(false);
  const [disablePermissions, setDisablePermissions] = useState(false);
  const { addAlert } = useAlert();

  const [permissions, setPermissions] = useState({
    is_admin: false,
    view_material_management: true,
    view_worker_management: true,
    view_finance: true,
    view_all: false,
    edit_material_management: false,
    edit_worker_management: false,
    edit_finance: false,
    edit_all: false,
    create_taskgroup: false,
    report_generation: false,
    member_control: false,
    joined_as: "member",
  });

  useEffect(() => {
    fetchOrganizationMembers();
  }, []);

  const fetchOrganizationMembers = async () => {
    try {
      setLoading(true);
      const accessToken = localStorage.getItem("access");
      const organizationId = JSON.parse(
        localStorage.getItem("organization"),
      )?.id;
      const response = await fetch(
        `${BASE_URL}/org/organization/${organizationId}/members/`,
        { method: "GET", headers: { Authorization: `Bearer ${accessToken}` } },
      );
      if (!response.ok) throw new Error("Failed to fetch organization members");
      const data = await response.json();
      if (data.status && data.data) {
        const filteredMembers = data.data.filter(
          (member) =>
            !teams.some((t) => t.organization_member_id === member.id),
        );
        setOrganizationMembers(filteredMembers);
      } else throw new Error("Invalid data format");
    } catch (error) {
      setError(error.message);
      addAlert("Error fetching members", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleMemberSelect = (member) => {
    setSelectedMember(member);
    setPermissions((prev) => ({
      ...prev,
      joined_as: member?.user?.profession || member?.profession,
    }));
    setShowPermissions(true);
  };

  const handlePermissionChange = (key) => {
    let updatedPermissions = { ...permissions };

    if (key === "is_admin") {
      const newAdminVal = !permissions.is_admin;
      if (newAdminVal) {
        updatedPermissions = {
          ...permissions,
          is_admin: true,
          view_material_management: true,
          view_worker_management: true,
          view_finance: true,
          view_all: true,
          edit_material_management: true,
          edit_worker_management: true,
          edit_finance: true,
          edit_all: true,
          create_taskgroup: true,
          report_generation: true,
          member_control: true,
        };
        setDisablePermissions(true);
      } else {
        updatedPermissions = { ...permissions, is_admin: false };
        setDisablePermissions(false);
      }
    } else if (key === "view_all" && !disablePermissions) {
      const newVal = !permissions.view_all;
      updatedPermissions = {
        ...permissions,
        view_all: newVal,
        view_material_management: newVal,
        view_worker_management: newVal,
        view_finance: newVal,
        // If turning view_all OFF, also remove all edit permissions
        ...(!newVal && {
          edit_all: false,
          edit_material_management: false,
          edit_worker_management: false,
          edit_finance: false,
        }),
      };
    } else if (key === "edit_all" && !disablePermissions) {
      const newVal = !permissions.edit_all;
      updatedPermissions = {
        ...permissions,
        edit_all: newVal,
        edit_material_management: newVal,
        edit_worker_management: newVal,
        edit_finance: newVal,
        create_taskgroup: newVal,
        report_generation: newVal,
        member_control: newVal,
        // When edit_all turned ON → also grant view_all + all individual views
        // When turned OFF → leave view permissions untouched
        ...(newVal && {
          view_all: true,
          view_material_management: true,
          view_worker_management: true,
          view_finance: true,
        }),
      };
    } else {
      if (!permissions.is_admin) {
        updatedPermissions[key] = !permissions[key];

        // view_* turned OFF → also turn OFF corresponding edit_*
        if (key.startsWith("view_") && !updatedPermissions[key]) {
          const editKey = `edit_${key.replace("view_", "")}`;
          if (updatedPermissions[editKey] !== undefined) {
            updatedPermissions[editKey] = false;
          }
        }

        // edit_* turned ON → also enable corresponding view_*
        if (key.startsWith("edit_") && updatedPermissions[key]) {
          const viewKey = `view_${key.replace("edit_", "")}`;
          if (updatedPermissions[viewKey] !== undefined) {
            updatedPermissions[viewKey] = true;
          }
        }

        // Recalculate view_all: true only when ALL 3 individual views are true
        updatedPermissions.view_all =
          updatedPermissions.view_material_management &&
          updatedPermissions.view_worker_management &&
          updatedPermissions.view_finance;

        // Recalculate edit_all
        updatedPermissions.edit_all =
          updatedPermissions.edit_material_management &&
          updatedPermissions.edit_worker_management &&
          updatedPermissions.edit_finance &&
          updatedPermissions.create_taskgroup &&
          updatedPermissions.report_generation &&
          updatedPermissions.member_control;
      }
    }

    setPermissions(updatedPermissions);
  };

  const handleAddMember = async () => {
    try {
      const accessToken = localStorage.getItem("access");
      const siteId = localStorage.getItem("site");
      const requestBody = {
        ...permissions,
        organization_member: selectedMember.id,
        sites: parseInt(siteId),
      };
      const response = await fetch(`${BASE_URL}/team/add-member/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(requestBody),
      });
      if (!response.ok) throw new Error("Failed to add member");
      addAlert("Member added successfully!", "success");
      onSave && onSave();
      onClose();
    } catch (error) {
      setError(error.message);
      addAlert("Error adding member", "error");
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center"
      style={{ zIndex: 1000 }}
    >
      <div
        className="bg-[#F5F5F5] rounded-2xl w-full max-w-xl h-[98vh]"
        style={{ overflowY: "auto" }}
      >
        {/* Header */}
        <div className="flex items-center bg-white justify-between p-4 border-b rounded-t-2xl">
          <button onClick={onClose} className="p-1">
            <X className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold">
            {showPermissions ? "Set Permissions" : "Add Member"}
          </h2>
          <div className="p-1 w-5 h-5" />
        </div>

        {/* Content */}
        <div className="p-4">
          {!showPermissions ? (
            <div>
              <div className="bg-white rounded-lg p-4 mb-4">
                <h3 className="font-medium text-gray-800 mb-2">
                  Organization Members
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  Select a member to add to your team
                </p>
              </div>
              <div className="space-y-2 h-[60vh]" style={{ overflowY: "auto" }}>
                {loading ? (
                  <div className="text-center py-4">Loading members...</div>
                ) : error ? (
                  <div className="text-center py-4 text-red-500">{error}</div>
                ) : organizationMembers.length === 0 ? (
                  <div className="text-center py-4">
                    Please add organization members from Global Dashboard to add
                    them in your projects.
                  </div>
                ) : (
                  organizationMembers.map((member) => (
                    <div
                      key={member.id}
                      className="flex flex-col p-3 bg-white hover:bg-gray-50 rounded-lg cursor-pointer"
                      onClick={() => handleMemberSelect(member)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {member.user.userpic ? (
                            <img
                              src={member.user.userpic}
                              alt={`${member.user.first_name} ${member.user.last_name}`}
                              className="w-10 h-10 rounded-full"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <User className="w-6 h-6 text-gray-600" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium">
                              {member.user.first_name} {member.user.last_name}
                            </p>
                            <p className="text-sm text-gray-500">
                              {member.user.email}
                            </p>
                          </div>
                        </div>
                        <div
                          className="rounded-2xl flex flex-row text-xs font-medium text-black items-center justify-center px-3 py-1"
                          style={{
                            background: member.is_admin ? "#C8E3CA" : "#FFFF8C",
                          }}
                        >
                          {member.is_admin
                            ? "Admin"
                            : member?.user?.profession || member?.profession}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Selected member info */}
              <div className="bg-white rounded-lg p-4 mb-6">
                <h3 className="text-black font-bold text-[14px] mb-4">
                  Selected Member
                </h3>
                <div className="flex items-center gap-3">
                  {selectedMember.user.userpic ? (
                    <img
                      src={selectedMember.user.userpic}
                      alt={`${selectedMember.user.first_name} ${selectedMember.user.last_name}`}
                      className="w-14 h-14 rounded-full"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-gray-300 flex items-center justify-center">
                      <User className="w-8 h-8 text-gray-600" />
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-base">
                      {selectedMember.user.first_name}{" "}
                      {selectedMember.user.last_name}
                    </p>
                    <p className="text-sm text-gray-500">
                      {selectedMember.user.email}
                    </p>
                    <p className="text-sm text-gray-500">
                      (
                      {selectedMember.is_admin
                        ? "Admin"
                        : selectedMember?.user?.profession || "Member"}
                      )
                    </p>
                  </div>
                </div>
              </div>

              {/* Role */}
              <div className="rounded p-3 bg-white">
                <h2 className="text-black font-bold">Role</h2>
                <p className="text-sm text-[#232323] mb-4">
                  Define member role in team
                </p>
                <div className="mb-4">
                  <label className="flex items-center justify-between p-2">
                    <div className="flex flex-col items-start">
                      <span className="font-medium text-[13px]">Admin</span>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Full access to all features and settings
                      </p>
                    </div>
                    {permissions.is_admin ? (
                      <IoCheckbox
                        className="w-5 h-5 rounded text-purple-900"
                        onClick={() => handlePermissionChange("is_admin")}
                      />
                    ) : (
                      <MdCheckBoxOutlineBlank
                        className="w-5 h-5 rounded text-purple-900"
                        onClick={() => handlePermissionChange("is_admin")}
                      />
                    )}
                  </label>
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role
                    </label>
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100">
                      {selectedMember?.user?.profession || "Member"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Task Authority */}
              <div className="rounded p-3 bg-white">
                <label className="flex items-center justify-between p-2">
                  <div className="flex flex-col items-start">
                    <span className="capitalize font-medium text-[13px]">
                      Task Authority
                    </span>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Create and assign task groups to team members
                    </p>
                  </div>
                  {permissions.create_taskgroup ? (
                    <IoCheckbox
                      className="w-5 h-5 rounded text-purple-900"
                      onClick={() =>
                        !disablePermissions &&
                        setPermissions({
                          ...permissions,
                          create_taskgroup: false,
                        })
                      }
                    />
                  ) : (
                    <MdCheckBoxOutlineBlank
                      className="w-5 h-5 rounded text-purple-900"
                      onClick={() =>
                        !disablePermissions &&
                        setPermissions({
                          ...permissions,
                          create_taskgroup: true,
                        })
                      }
                    />
                  )}
                </label>
              </div>

              {/* Report Generation */}
              <div className="rounded p-3 bg-white">
                <label className="flex items-center justify-between p-2">
                  <div className="flex flex-col items-start">
                    <span className="capitalize font-medium text-[13px]">
                      Report Generation
                    </span>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Generate and download site progress reports
                    </p>
                  </div>
                  {permissions.report_generation ? (
                    <IoCheckbox
                      className="w-5 h-5 rounded text-purple-900"
                      onClick={() =>
                        setPermissions({
                          ...permissions,
                          report_generation: false,
                        })
                      }
                    />
                  ) : (
                    <MdCheckBoxOutlineBlank
                      className="w-5 h-5 rounded text-purple-900"
                      onClick={() =>
                        setPermissions({
                          ...permissions,
                          report_generation: true,
                        })
                      }
                    />
                  )}
                </label>
              </div>

              {/* View permissions */}
              <div className="rounded p-3 bg-white">
                <h2 className="text-black font-bold">View Permissions</h2>
                <p className="text-sm text-[#232323] mb-4">
                  Control what the member can view
                </p>
                {[
                  { key: "view_all", label: "All", desc: null },
                  {
                    key: "view_material_management",
                    label: "Material management",
                    desc: "View material inventory, purchases, and usage logs",
                  },
                  {
                    key: "view_worker_management",
                    label: "Worker management",
                    desc: "View worker profiles, attendance, and daily wages",
                  },
                  {
                    key: "view_finance",
                    label: "Finance",
                    desc: "View financial transactions, budgets, and cost reports",
                  },
                ].map(({ key, label, desc }) => (
                  <label
                    key={key}
                    className="flex items-center justify-between p-2"
                  >
                    <div className="flex flex-col items-start">
                      <span className="capitalize font-medium text-[13px]">
                        {label}
                      </span>
                      {desc && (
                        <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                      )}
                    </div>
                    {permissions[key] ? (
                      <IoCheckbox
                        className="w-5 h-5 rounded text-purple-900 flex-shrink-0"
                        onClick={() => handlePermissionChange(key)}
                      />
                    ) : (
                      <MdCheckBoxOutlineBlank
                        className="w-5 h-5 rounded text-purple-900 flex-shrink-0"
                        onClick={() => handlePermissionChange(key)}
                      />
                    )}
                  </label>
                ))}
              </div>

              {/* Edit permissions */}
              <div className="rounded p-3 bg-white">
                <h2 className="text-black font-bold">Edit Permissions</h2>
                <p className="text-sm text-[#232323] mb-4">
                  Control what the member can edit
                </p>
                {[
                  { key: "edit_all", label: "All", desc: null },
                  {
                    key: "edit_material_management",
                    label: "Material management",
                    desc: "Add, update, and delete material entries",
                  },
                  {
                    key: "edit_worker_management",
                    label: "Worker management",
                    desc: "Manage worker records and attendance",
                  },
                  {
                    key: "edit_finance",
                    label: "Finance",
                    desc: "Create and modify financial entries",
                  },
                  {
                    key: "member_control",
                    label: "Member control",
                    desc: "Add, remove, and edit team member permissions",
                  },
                ].map(({ key, label, desc }) => (
                  <label
                    key={key}
                    className="flex items-center justify-between p-2"
                  >
                    <div className="flex flex-col items-start">
                      <span className="capitalize font-medium text-[13px]">
                        {label}
                      </span>
                      {desc && (
                        <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                      )}
                    </div>
                    {permissions[key] ? (
                      <IoCheckbox
                        className="w-5 h-5 rounded text-purple-900 flex-shrink-0"
                        onClick={() => handlePermissionChange(key)}
                      />
                    ) : (
                      <MdCheckBoxOutlineBlank
                        className="w-5 h-5 rounded text-purple-900 flex-shrink-0"
                        onClick={() => handlePermissionChange(key)}
                      />
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Button */}
        <div className="p-4">
          {showPermissions && (
            <div className="flex gap-4">
              <button
                onClick={() => setShowPermissions(false)}
                className="w-1/2 bg-gray-200 text-gray-800 font-semibold rounded-lg py-3"
              >
                Back
              </button>
              <button
                onClick={handleAddMember}
                className="w-1/2 bg-[#3B0069] text-white font-semibold rounded-lg py-3 flex items-center justify-center gap-2"
              >
                Add Member
                <div className="rounded-full p-1 flex bg-text-clr flex-row items-center">
                  <Check className="w-4 h-4" />
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── TeamsModal ───────────────────────────────────────────────────────────────
export const TeamsModal = ({ onClose, teams = [], onMemberClick }) => {
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [gpsEnabled, setGpsEnabled] = useState(false);

  const setSiteGPSPreference = async (siteId, enabled) => {
    try {
      await rtdb
        .ref(`GPSPreferences-${environment}/Sites/${siteId}`)
        .set(enabled);
      return true;
    } catch (error) {
      console.error("Error setting GPS preference:", error);
      return false;
    }
  };

  const getSiteGPSPreference = async (siteId) => {
    try {
      const snapshot = await rtdb
        .ref(`GPSPreferences-${environment}/Sites/${siteId}`)
        .once("value");
      const enabled = snapshot.val();
      return enabled !== null ? enabled : false;
    } catch (error) {
      console.error("Error getting GPS preference:", error);
      return false;
    }
  };

  useEffect(() => {
    const fetchGPSPreference = async () => {
      const siteId = localStorage.getItem("site");
      const enabled = await getSiteGPSPreference(siteId);
      setGpsEnabled(enabled);
    };
    fetchGPSPreference();
  }, []);

  const handleGPSToggle = async () => {
    const siteId = localStorage.getItem("site");
    const newGpsEnabled = !gpsEnabled;
    setGpsEnabled(newGpsEnabled);
    await setSiteGPSPreference(siteId, newGpsEnabled);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center"
      style={{ zIndex: 1000 }}
    >
      <div className="bg-[#F5F5F5] rounded-2xl w-full max-w-xl">
        <div className="flex items-center bg-white justify-between p-4 border-b rounded-t-2xl">
          <button onClick={onClose} className="p-1">
            <X className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold">Teams</h2>
          <div className="p-1 w-5 h-5" />
        </div>
        <div className="p-4">
          <div className="bg-white rounded-lg p-4 flex justify-between items-center mb-6">
            <p className="text-gray-700">{teams.length} members in your team</p>
          </div>
          <div className="space-y-4 h-[56vh]" style={{ overflowY: "auto" }}>
            {teams.map((member) => (
              <div
                key={member.member_id}
                className="flex flex-col py-3 px-3 bg-white hover:bg-gray-50 rounded-lg cursor-pointer"
                onClick={() => onMemberClick(member)}
              >
                <div className="flex items-center pb-3 justify-between border-b-[0.5px] border-[#A3A3A3]">
                  <div className="flex items-center gap-3">
                    {member?.userpic ? (
                      <img
                        src={member.userpic}
                        alt={`${member.first_name} ${member.last_name}`}
                        className="w-10 h-10 rounded-full"
                      />
                    ) : (
                      <User className="w-10 h-10 rounded-full bg-gray-300" />
                    )}
                    <div>
                      <p className="font-medium">
                        {member.first_name} {member.last_name}
                      </p>
                      <p className="text-sm text-gray-500">
                        +91-{member.contact_no}
                      </p>
                    </div>
                  </div>
                  <div className="w-5 h-5" />
                </div>
                <div className="flex items-center justify-start gap-3 mt-2">
                  {member.is_admin ? (
                    <>
                      <div
                        className="rounded-2xl flex flex-row text-[15px] font-medium text-black items-center justify-center px-3 py-1"
                        style={{ background: "#C8E3CA" }}
                      >
                        Admin
                      </div>
                      {member.org_admin && (
                        <div className="rounded-2xl flex flex-row text-[15px] font-medium text-[#6B21A8] items-center justify-center px-3 py-1 bg-[#F3E8FF]">
                          Super Admin
                        </div>
                      )}
                    </>
                  ) : (
                    <div
                      className="rounded-2xl flex flex-row text-[15px] font-medium text-black items-center justify-center px-3 py-1"
                      style={{ background: "#FFFF8C" }}
                    >
                      {member?.user?.profession || member?.profession}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="p-4">
          <button
            onClick={() => setShowAddMemberModal(true)}
            className="w-full bg-[#3B0069] text-white font-semibold rounded-lg py-3 flex items-center justify-center gap-2"
          >
            Add Member
            <div className="rounded-full p-1 flex bg-text-clr flex-row items-center">
              <Plus className="w-4 h-4" />
            </div>
          </button>
        </div>
        {showAddMemberModal && (
          <AddMemberModal
            onClose={() => setShowAddMemberModal(false)}
            teams={teams}
            onSave={() => {
              onClose();
            }}
          />
        )}
      </div>
    </div>
  );
};

// ─── AccessManagementModal ────────────────────────────────────────────────────
export const AccessManagementModal = ({
  member,
  onClose,
  onSave,
  variant = "modal",
}) => {
  const { addAlert } = useAlert();
  const isDrawer = variant === "drawer";

  const ICONS = {
    is_admin: article,
    view_all: article,
    view_material_management: group,
    view_worker_management: group,
    view_finance: finance,
    edit_all: article,
    edit_material_management: group,
    edit_worker_management: group,
    edit_finance: finance,
    create_taskgroup: article,
    report_generation: article,
    member_control: group,
  };

  const [permissions, setPermissions] = useState({
    is_admin: Boolean(member.is_admin),
    view_all: Boolean(member.view_all),
    view_material_management: Boolean(member.view_material_management),
    view_worker_management: Boolean(member.view_worker_management),
    view_finance: Boolean(member.view_finance),
    edit_all: Boolean(member.edit_all),
    edit_material_management: Boolean(member.edit_material_management),
    edit_worker_management: Boolean(member.edit_worker_management),
    edit_finance: Boolean(member.edit_finance),
    create_taskgroup: Boolean(member.create_taskgroup),
    report_generation: Boolean(member.report_generation),
    member_control: Boolean(member.member_control),
  });

  const showAdminAlert = () => {
    addAlert(
      "Please disable Admin permission first to modify individual permissions",
      "warning",
    );
  };

  const showSuperAdminAlert = () => {
    addAlert("Disable super admin permission first to uncheck", "warning");
  };

  const handlePermissionToggle = (key) => {
    if (permissions.is_admin && key !== "is_admin") {
      showAdminAlert();
      return;
    }

    if (key === "view_all") {
      const newVal = !permissions.view_all;
      setPermissions({
        ...permissions,
        view_all: newVal,
        view_material_management: newVal,
        view_worker_management: newVal,
        view_finance: newVal,
      });
      return;
    }

    // edit_all ON → also grant view_all + all views; OFF → only remove edit perms, leave views untouched
    if (key === "edit_all") {
      const newVal = !permissions.edit_all;
      setPermissions({
        ...permissions,
        edit_all: newVal,
        edit_material_management: newVal,
        edit_worker_management: newVal,
        edit_finance: newVal,
        create_taskgroup: newVal,
        report_generation: newVal,
        member_control: newVal,
        ...(newVal && {
          view_all: true,
          view_material_management: true,
          view_worker_management: true,
          view_finance: true,
        }),
      });
      return;
    }

    if (key.startsWith("view_") && key !== "view_all") {
      setPermissions((prev) => {
        const next = { ...prev, [key]: !prev[key] };

        if (!next[key]) {
          // View turned OFF → also turn OFF corresponding edit + clear view_all + edit_all
          const editKey = `edit_${key.replace("view_", "")}`;
          if (next[editKey] !== undefined) next[editKey] = false;
          next.view_all = false;
          next.edit_all = false;
        } else {
          // View turned ON → recalculate view_all
          if (
            next.view_material_management &&
            next.view_worker_management &&
            next.view_finance
          ) {
            next.view_all = true;
          }
        }
        return next;
      });
      return;
    }

    // Individual edit_*: turning ON also enables corresponding view
    if (key.startsWith("edit_") && key !== "edit_all") {
      setPermissions((prev) => {
        const next = { ...prev, [key]: !prev[key] };

        if (!next[key]) {
          // Edit turned OFF → clear edit_all
          next.edit_all = false;
        } else {
          // Edit turned ON → also enable corresponding view
          const viewKey = `view_${key.replace("edit_", "")}`;
          if (next[viewKey] !== undefined) {
            next[viewKey] = true;
            // Recalculate view_all
            if (
              next.view_material_management &&
              next.view_worker_management &&
              next.view_finance
            ) {
              next.view_all = true;
            }
          }
          // Recalculate edit_all
          const editKeys = [
            "edit_material_management",
            "edit_worker_management",
            "edit_finance",
            "create_taskgroup",
            "report_generation",
            "member_control",
          ];
          if (editKeys.every((k) => next[k])) next.edit_all = true;
        }
        return next;
      });
      return;
    }

    if (
      ["create_taskgroup", "report_generation", "member_control"].includes(key)
    ) {
      setPermissions((prev) => {
        const next = { ...prev, [key]: !prev[key] };
        const editKeys = [
          "edit_material_management",
          "edit_worker_management",
          "edit_finance",
          "create_taskgroup",
          "report_generation",
          "member_control",
        ];
        next.edit_all = editKeys.every((permissionKey) => next[permissionKey]);
        return next;
      });
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "N/A";
    const hours = d.getHours();
    const minutes = d.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return `${hours % 12}:${String(minutes).padStart(2, "0")} ${ampm}, ${d.getDate()} ${months[d.getMonth()]}, ${d.getFullYear()}`;
  };

  const handleSave = async () => {
    try {
      const accessToken = localStorage.getItem("access");
      const requestBody = { ...permissions };
      requestBody.sites = localStorage.getItem("site");
      requestBody.organization_member = member?.organization_member_id;
      const response = await fetch(
        `${BASE_URL}/team/update-member/${member.member_id}/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(requestBody),
        },
      );
      if (response.ok) {
        addAlert("Success!", "success");
        onSave();
        onClose();
      }
    } catch (error) {
      console.error("Error updating permissions:", error);
    }
  };

  const handleRemoveMember = async () => {
    try {
      const accessToken = localStorage.getItem("access");
      const response = await fetch(
        `${BASE_URL}/team/delete-member/${member.member_id}/`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      if (response.ok) {
        onSave();
        onClose();
      }
    } catch (error) {
      console.error("Error removing member:", error);
    }
  };

  // Helper: render a permission row with icon + label + inline description + checkbox
  const PermissionRow = ({ permKey, label }) => {
    const value = permissions[permKey];
    const description = PERMISSION_DESCRIPTIONS[permKey];
    const isAll = permKey.endsWith("_all");
    return (
      <label className="flex items-center justify-between p-2">
        <div className="flex flex-row items-start gap-2">
          <div className="bg-[#C5B3D2] rounded-full p-1 mt-0.5">
            <img
              src={ICONS[permKey] || article}
              alt="icon"
              className="w-5 h-5"
            />
          </div>
          <div className="flex flex-col items-start">
            <span className="capitalize font-medium text-[13px]">{label}</span>
            {!isAll && description && (
              <p className="text-xs text-gray-500 mt-0.5">{description}</p>
            )}
          </div>
        </div>
        {value ? (
          <IoCheckbox
            className="w-5 h-5 rounded text-purple-900 flex-shrink-0"
            onClick={() => handlePermissionToggle(permKey)}
          />
        ) : (
          <MdCheckBoxOutlineBlank
            className="w-5 h-5 rounded text-purple-900 flex-shrink-0"
            onClick={() => handlePermissionToggle(permKey)}
          />
        )}
      </label>
    );
  };

  return (
    <div
      className={
        isDrawer
          ? "fixed inset-0 bg-black/20"
          : "fixed inset-0 bg-black/50 flex items-center justify-center"
      }
      style={{ zIndex: 50 }}
    >
      <div
        className={
          isDrawer
            ? "absolute right-0 top-0 flex h-full w-full max-w-[520px] flex-col bg-[#F5F5F5] shadow-2xl"
            : "bg-[#F5F5F5] rounded-2xl w-[50%]"
        }
      >
        <div
          className={`flex bg-white items-center justify-between px-3 py-[20px] border-b ${
            isDrawer ? "" : "rounded-t-2xl"
          }`}
        >
          <button onClick={onClose} className="p-1">
            <X className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold">Access Management</h2>
          <div className="p-1 w-5 h-5" />
        </div>

        <div
          className={isDrawer ? "flex-1 px-4 py-5" : "px-12 py-6 h-[70vh]"}
          style={{ overflowY: "auto" }}
        >
          {/* Employee details */}
          <div className="mb-8 px-6 pt-3 bg-white rounded-xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-black font-bold text-[14px]">
                Employee details
              </h3>
              <button
                onClick={handleRemoveMember}
                style={{
                  display: member.joined_as === "owner" ? "none" : "flex",
                }}
                className="bg-[#F9E8E8] w-40 py-2 flex flex-row items-center gap-2 justify-center rounded-xl hover:bg-red-50"
              >
                <p className="text-[#C21313] text-[12px] font-bold">Remove</p>
                <LuUserMinus className="w-4 h-4 text-[#C21313]" />
              </button>
            </div>
            <div className="flex items-center pb-6 border-b-[1px] border-[#CCCCCC] justify-between">
              <div className="flex items-center gap-3">
                {getMemberAvatar(member) ? (
                  <img
                    src={getMemberAvatar(member)}
                    alt={getMemberName(member)}
                    className="w-14 h-14 rounded-full"
                    style={{
                      objectFit:"cover"
                    }}
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-gray-300 flex items-center justify-center">
                    <span className="text-[#3B0069] text-xl font-medium">
                      {getInitials(getMemberName(member))}
                    </span>
                  </div>
                )}
                <div>
                  <p className="font-semibold text-base">
                    {getMemberName(member)}
                  </p>
                  <p className="text-sm text-gray-500">
                    {getMemberEmail(member)}
                  </p>
                  {/* <p className="text-sm text-gray-500">{member?.contact_no}</p> */}
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-gray-500">
                      (
                      {member.is_admin
                        ? "Admin"
                        : member?.user?.profession || member?.profession}
                      )
                    </p>
                    {member.is_admin && member.org_admin && (
                      <span className="text-[11px] font-medium text-[#6B21A8] bg-[#F3E8FF] rounded-full px-2 py-0.5">
                        Super Admin
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-row pt-4 py-4 items-center justify-between">
              <p className="text-[14px] font-normal text-[#737373]">
                Joined / Added on
              </p>
              <p className="text-[14px] font-medium text-black">
                {formatDate(member.created_on)}
              </p>
            </div>
          </div>

          {/* Permissions */}
          <div className="space-y-6">
            {/* Admin */}
            <div className="rounded p-3 bg-white">
              <label className="flex items-center justify-between p-2">
                <div className="flex flex-row items-start gap-2">
                  <div className="bg-[#C5B3D2] rounded-full p-1">
                    <img
                      src={ICONS["is_admin"]}
                      alt="icon"
                      className="w-5 h-5"
                    />
                  </div>
                  <div className="flex flex-col mt-1 items-start gap-1">
                    <div className="flex items-center gap-2">
                      <span className="capitalize font-medium text-[13px]">
                        Admin
                      </span>
                      {member.org_admin && (
                        <span className="text-[11px] font-medium text-[#6B21A8] bg-[#F3E8FF] rounded-full px-2 py-0.5">
                          Super Admin
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      Full access to all features and settings
                    </p>
                  </div>
                </div>
                {permissions.is_admin ? (
                  <IoCheckbox
                    className="w-5 h-5 rounded text-purple-900"
                    onClick={() => {
                      if (member.org_admin) {
                        showSuperAdminAlert();
                        return;
                      }
                      setPermissions({ ...permissions, is_admin: false });
                    }}
                  />
                ) : (
                  <MdCheckBoxOutlineBlank
                    className="w-5 h-5 rounded text-purple-900"
                    onClick={() =>
                      setPermissions({
                        is_admin: true,
                        create_taskgroup: true,
                        report_generation: true,
                        view_all: true,
                        edit_all: true,
                        view_finance: true,
                        view_material_management: true,
                        view_worker_management: true,
                        edit_finance: true,
                        edit_material_management: true,
                        edit_worker_management: true,
                        member_control: true,
                      })
                    }
                  />
                )}
              </label>
            </div>

            {/* Task Authority */}
            <div className="rounded p-3 bg-white">
              <label className="flex items-center justify-between p-2">
                <div className="flex flex-row items-start gap-2">
                  <div className="bg-[#C5B3D2] rounded-full p-1">
                    <img
                      src={ICONS["create_taskgroup"]}
                      alt="icon"
                      className="w-5 h-5"
                    />
                  </div>
                  <div className="flex flex-col mt-1 items-start gap-1">
                    <span className="capitalize font-medium text-[13px]">
                      Task Authority
                    </span>
                    <p className="text-xs text-gray-500">
                      Create and manage task groups
                    </p>
                  </div>
                </div>
                {permissions.create_taskgroup ? (
                  <IoCheckbox
                    className="w-5 h-5 rounded text-purple-900"
                    onClick={() => {
                      if (permissions.is_admin) {
                        showAdminAlert();
                        return;
                      }
                      handlePermissionToggle("create_taskgroup");
                    }}
                  />
                ) : (
                  <MdCheckBoxOutlineBlank
                    className="w-5 h-5 rounded text-purple-900"
                    onClick={() =>
                      !permissions.is_admin &&
                      handlePermissionToggle("create_taskgroup")
                    }
                  />
                )}
              </label>
            </div>

            {/* Report Generation */}
            <div className="rounded p-3 bg-white">
              <label className="flex items-center justify-between p-2">
                <div className="flex flex-row items-start gap-2">
                  <div className="bg-[#C5B3D2] rounded-full p-1">
                    <img
                      src={ICONS["report_generation"]}
                      alt="icon"
                      className="w-5 h-5"
                    />
                  </div>
                  <div className="flex flex-col mt-1 items-start gap-1">
                    <span className="capitalize font-medium text-[13px]">
                      Report Generation
                    </span>
                    <p className="text-xs text-gray-500">
                      Generate and access site reports
                    </p>
                  </div>
                </div>
                {permissions.report_generation ? (
                  <IoCheckbox
                    className="w-5 h-5 rounded text-purple-900"
                    onClick={() => {
                      if (permissions.is_admin) {
                        showAdminAlert();
                        return;
                      }
                      handlePermissionToggle("report_generation");
                    }}
                  />
                ) : (
                  <MdCheckBoxOutlineBlank
                    className="w-5 h-5 rounded text-purple-900"
                    onClick={() =>
                      !permissions.is_admin &&
                      handlePermissionToggle("report_generation")
                    }
                  />
                )}
              </label>
            </div>

            {/* View section */}
            <div className="rounded p-3 bg-white">
              <h2 className="text-black font-bold">View</h2>
              <p className="text-sm text-[#232323] mb-4">
                control what the member can view
              </p>
              {Object.entries(permissions)
                .filter(([key]) => key.startsWith("view_"))
                .map(([key]) => {
                  const labelMap = {
                    view_all: "all",
                    view_material_management: "material management",
                    view_worker_management: "worker management",
                    view_finance: "finance",
                  };
                  return (
                    <PermissionRow
                      key={key}
                      permKey={key}
                      label={
                        labelMap[key] ||
                        key.replace("view_", "").replace(/_/g, " ")
                      }
                    />
                  );
                })}
            </div>

            {/* Edit section */}
            <div className="rounded p-3 bg-white">
              <h2 className="text-black font-bold">Edit</h2>
              <p className="text-sm text-[#232323] mb-4">
                control what the member can edit
              </p>
              {Object.entries(permissions)
                .filter(([key]) => key.startsWith("edit_"))
                .map(([key]) => {
                  const labelMap = {
                    edit_all: "all",
                    edit_material_management: "material management",
                    edit_worker_management: "worker management",
                    edit_finance: "finance",
                  };
                  return (
                    <PermissionRow
                      key={key}
                      permKey={key}
                      label={
                        labelMap[key] ||
                        key.replace("edit_", "").replace(/_/g, " ")
                      }
                    />
                  );
                })}
              <PermissionRow permKey="member_control" label="member control" />
            </div>
          </div>

          {/* Save button */}
          <div className="p-4 border-t">
            <button
              onClick={handleSave}
              className="w-full bg-[#3B0069] text-white font-semibold rounded-lg py-3 flex items-center justify-center gap-2"
            >
              Save changes
              <div className="flex flex-row items-center p-1 rounded-full bg-text-clr">
                <Check className="w-4 h-4" />
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
